import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {P} from 'UI/components/layout/Text';

import {ChevronRightIcon, PaymentsIcon, ScheduleIcon} from 'Images/icons';

/**
 * The props for the `TourListItem` component.
 * Configures a compact tour row for the "All Tours" list.
 */
interface ComponentProps {
    /** The tour category label (e.g. `"Architecture"`). */
    category: string;
    /** Optional icon element rendered before the category label. */
    categoryIcon?: ReactNode;
    /** The formatted walking duration (e.g. `"30 min"`). */
    duration: string;
    /** Optional click handler for the entire row. */
    onClick?(): void;
    /** The formatted price label (e.g. `"Free"` or `"€2.99"`). */
    price: string;
    /**
     * The `testId` property represents a unique identifier assigned
     * to a component for testing purposes. It is applied as `data-cy`
     * attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'TourListItem'
     */
    testId?: string;
    /** The URL of the thumbnail image (96×96). */
    thumbnailUrl: string;
    /** The tour title. */
    title: string;
}

/**
 * A compact tour list row with thumbnail, category label, title,
 * duration/price metadata and a right-chevron disclosure indicator.
 * Matches the "All Tours" section of the homepage design.
 *
 * @param props              The component props.
 * @param props.category     The tour category label.
 * @param props.categoryIcon Optional icon rendered before the category label.
 * @param props.duration     The formatted walking duration.
 * @param props.onClick      Optional click handler.
 * @param props.price        The formatted price label.
 * @param props.testId       Test ID for Cypress selectors. Defaults to `'TourListItem'`.
 * @param props.thumbnailUrl The thumbnail image URL.
 * @param props.title        The tour title.
 * @returns A JSX element rendering the TourListItem.
 *
 * @example
 * ```tsx
 * <TourListItem
 *     category="Architecture"
 *     duration="30 min"
 *     price="Free"
 *     thumbnailUrl="/images/thumb.jpg"
 *     title="Renaissance Masterpiece: Town Hall Square"
 * />
 * ```
 */
const TourListItem = ({
    category,
    categoryIcon,
    duration,
    onClick,
    price,
    testId = 'TourListItem',
    thumbnailUrl,
    title
}: ComponentProps) => (
    <Card data-cy={testId} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}>
        <Thumbnail alt={title} data-cy={`${testId}-thumbnail`} src={thumbnailUrl} />
        <Content>
            <CategoryLabel $size="labelMedium" as="span" data-cy={`${testId}-category`}>
                {categoryIcon && <CategoryIconWrapper>{categoryIcon}</CategoryIconWrapper>}
                {category}
            </CategoryLabel>
            <Title $size="bodySmall" as="span" data-cy={`${testId}-title`}>
                {title}
            </Title>
            <MetaRow>
                <MetaItem>
                    <ScheduleIcon height={14} width={14} />
                    <MetaText $size="labelSmall" as="span">{duration}</MetaText>
                </MetaItem>
                <MetaItem $accent>
                    <PaymentsIcon height={14} width={14} />
                    <MetaText $size="labelSmall" as="span">{price}</MetaText>
                </MetaItem>
            </MetaRow>
        </Content>
        <ChevronArea>
            <ChevronRightIcon height={24} width={24} />
        </ChevronArea>
    </Card>
);

TourListItem.displayName = 'TourListItem';

export {TourListItem};

// ── Styled ──────────────────────────────────────────────────

const Card = styled.div`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceLow};
    border-radius: 1.6rem;
    cursor: pointer;
    display: flex;
    gap: ${spacing(5)};
    padding: ${spacing(3)};
    transition: background 0.2s ease;

    &:hover {
        background: ${({theme}) => theme.colors.surfaceMid};
    }
`;

const Thumbnail = styled.img`
    border-radius: 1.2rem;
    display: block;
    flex-shrink: 0;
    height: 9.6rem;
    object-fit: cover;
    width: 9.6rem;
`;

const Content = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${spacing(1)};
    min-width: 0;
    padding: ${spacing(1)} 0;
`;

const CategoryLabel = styled(P)`
    align-items: center;
    color: ${({theme}) => theme.colors.accentDefault};
    display: inline-flex;
    font-size: 1rem;
    gap: ${spacing(1)};
    letter-spacing: 0.1rem;
`;

const CategoryIconWrapper = styled.span`
    align-items: center;
    display: inline-flex;

    svg {
        height: 1.2rem;
        width: 1.2rem;
    }
`;

const Title = styled(P)`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-weight: 700;
    line-height: 1.3;
`;

const MetaRow = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(3)};
    margin-top: ${spacing(1)};
`;

interface MetaItemProps {
    /** Whether to use the primary accent color. */
    $accent?: boolean;
}

const MetaItem = styled.span<MetaItemProps>`
    align-items: center;
    color: ${({$accent, theme}) => ($accent ? theme.colors.primaryDefault : theme.colors.inkTertiary)};
    display: flex;
    gap: ${spacing(1)};
`;

const MetaText = styled(P)`
    color: inherit;
    font-weight: 500;
`;

const ChevronArea = styled.div`
    color: ${({theme}) => theme.colors.inkDisabled};
    flex-shrink: 0;
    padding-right: ${spacing(2)};
    transition: color 0.2s ease;

    ${Card}:hover & {
        color: ${({theme}) => theme.colors.primaryDefault};
    }
`;